import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDealerLocations, DealerLocation } from '../services/public';
import { isAuthenticated, isAdmin } from '../services/auth';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import productInsertsImg from '../assets/product-inserts.jpg';
import heroPanoramaImg from '../assets/hero-panorama.jpg';
import heroPanoramaPortraitImg from '../assets/hero-panorama-portrait.jpg';
import './LandingPage.css';
import BrandLogo from '../components/BrandLogo';
import { useReveal } from '../hooks/useReveal';

const BENEFITS = [
  {
    num: '01',
    title: 'Volles Sichtfeld',
    body: 'Keine Fassung im Blick, kein toter Winkel. Deine Sehstärke sitzt direkt in der Goggle — du siehst die ganze Line.',
  },
  {
    num: '02',
    title: 'Kein Beschlagen',
    body: 'Kein Luftraum zwischen Brille und Goggle, in dem sich Feuchtigkeit staut. Klarer Blick von der ersten bis zur letzten Abfahrt.',
  },
];

// Komponente, die die Karte automatisch auf alle Marker zoomen lässt
const MapBounds: React.FC<{ dealers: DealerLocation[] }> = ({ dealers }) => {
  const map = useMap();

  useEffect(() => {
    const dealersWithCoords = dealers.filter(
      d => typeof d.latitude === 'number' && typeof d.longitude === 'number'
    );

    if (dealersWithCoords.length > 0) {
      const bounds = L.latLngBounds(
        dealersWithCoords.map(d => [d.latitude!, d.longitude!] as [number, number])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [dealers, map]);

  return null;
};

const LandingPage: React.FC = () => {
  const [dealerLocations, setDealerLocations] = useState<DealerLocation[]>([]);
  const [loadingDealers, setLoadingDealers] = useState(true);
  const [dealersError, setDealersError] = useState<string | null>(null);

  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);

  const dealersWithCoords = useMemo(
    () =>
      Array.isArray(dealerLocations)
        ? dealerLocations.filter(
            d => typeof d.latitude === 'number' && typeof d.longitude === 'number'
          )
        : [],
    [dealerLocations]
  );

  const mapCenter = useMemo(() => {
    if (dealersWithCoords.length > 0) {
      const avgLat =
        dealersWithCoords.reduce((sum, d) => sum + (d.latitude || 0), 0) / dealersWithCoords.length;
      const avgLng =
        dealersWithCoords.reduce((sum, d) => sum + (d.longitude || 0), 0) /
        dealersWithCoords.length;
      return [avgLat, avgLng] as [number, number];
    }
    // Grober Mittelpunkt Alpenraum
    return [47.0, 10.0] as [number, number];
  }, [dealersWithCoords]);

  // Marker-Icons lokal gebundelt (kein CDN — DSGVO)
  const DefaultIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: markerIcon,
        iconRetinaUrl: markerIcon2x,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
      }),
    []
  );

  useEffect(() => {
    const loadDealers = async () => {
      try {
        setLoadingDealers(true);
        const data = await getDealerLocations();
        setDealerLocations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading dealer locations:', error);
        setDealersError('Händlerstandorte konnten nicht geladen werden.');
        setDealerLocations([]);
      } finally {
        setLoadingDealers(false);
      }
    };

    loadDealers();
  }, []);

  // Hero-Bilder sofort dekodieren für einen sauberen ersten Paint
  useEffect(() => {
    [heroPanoramaImg, heroPanoramaPortraitImg].forEach(src => {
      const img = new Image();
      img.src = src;
      img.decode?.().catch(() => {});
    });
  }, []);

  // Scroll-Reveals
  useReveal(rootRef, [loadingDealers]);

  const handleEnterPortal = () => {
    if (isAuthenticated()) {
      if (isAdmin()) {
        navigate('/admin/orders');
      } else {
        navigate('/orders/new');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="landing-page" ref={rootRef}>
      <header className="landing-header">
        <a href="#top" className="landing-wordmark" aria-label="ELFHALBE — nach oben">
          <BrandLogo height={18} withIcon />
        </a>
        <nav className="landing-nav">
          <a href="#haendler">Händler</a>
          <a href="#b2b">Für Optiker</a>
          <button className="landing-login-btn" onClick={handleEnterPortal}>
            Login
          </button>
        </nav>
      </header>

      <main>
        {/* ============ 1. HERO — POV durch die Skibrille ============ */}
        <section id="top" className="hero-section">
          <div className="hero-photo" />
          <div className="hero-grain" />
          <div className="hero-scrim" />

          <div className="hero-content">
            <h1 className="hero-enter">
              Sieh, was du
              <br />
              verpasst hast.
            </h1>
            <p className="hero-sub hero-enter hero-enter-2">
              Optische Einsätze für deine Skibrille. Deine Sehstärke, unsichtbar integriert. Der
              Berg in voller Schärfe.
            </p>
            <div className="hero-actions hero-enter hero-enter-3">
              <a href="#haendler" className="btn btn-primary">
                Händler finden
              </a>
              <span className="hero-note">Verfügbar bei verifizierten Fachhändlern</span>
            </div>
          </div>

          <div className="hero-scroll-cue hero-enter hero-enter-4" aria-hidden="true">
            <span className="mono-label">Scroll</span>
            <span className="hero-scroll-line" />
          </div>
        </section>

        {/* ============ 2. PROBLEM / SOLUTION ============ */}
        <section className="problem-section">
          <div className="problem-grid">
            <div data-reveal="">
              <p className="eyebrow">Das Problem</p>
              <h2>Brille unter der Skibrille? Vergiss es.</h2>
              <p className="problem-text">
                Beschlagene Gläser. Druckstellen nach zwei Abfahrten. Kontaktlinsen, die bei
                −15&nbsp;°C austrocknen. Du fährst mit angezogener Handbremse.
              </p>
              <p className="problem-solution">
                ELFHALBE bringt deine Sehstärke <em>in die Brille</em> — zwei Präzisionsgläser,
                unsichtbar im Inneren deiner Goggle. Kein Aufsatz. Kein Kompromiss.
              </p>
            </div>
            <div className="problem-media" data-reveal="">
              <img
                src={productInsertsImg}
                alt="Skibrille von innen mit optischem Einsatz von ElfHalbe"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ============ 3. BENEFITS ============ */}
        <section className="benefits-section">
          <div className="benefits-inner">
            <h2 data-reveal="">
              Volle Sicht. <span className="benefits-ice">Null Kompromisse.</span>
            </h2>
            <div className="benefits-grid">
              {BENEFITS.map(b => (
                <div className="benefit-card" data-reveal="" key={b.num}>
                  <div className="benefit-bar" />
                  <p className="mono-label">{b.num}</p>
                  <h3>{b.title}</h3>
                  <p>{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 4. DEALER SECTION ============ */}
        <section id="haendler" className="dealer-section">
          <div className="dealer-intro" data-reveal="">
            <p className="eyebrow">Verifizierte Händler</p>
            <h2>Anpassung vom Profi. In deiner Nähe.</h2>
            <p>
              Deine Einsätze werden von zertifizierten Optikern und Sporthändlern exakt auf deine
              Werte gefertigt. Finde deinen Partner.
            </p>
          </div>
          <div className="dealer-layout" data-reveal="">
            <div className="dealer-map-container">
              {loadingDealers ? (
                <div className="loading">Lade Händlerstandorte...</div>
              ) : dealersError ? (
                <div className="dealer-map-placeholder-inner">
                  <span className="mono-label">
                    [ Interaktive Händlerkarte
                    <br />
                    DACH-Region ]
                  </span>
                </div>
              ) : dealersWithCoords.length === 0 ? (
                <div className="dealer-map-placeholder-inner">
                  <span className="mono-label">
                    [ Interaktive Händlerkarte
                    <br />
                    DACH-Region ]
                  </span>
                </div>
              ) : (
                <MapContainer
                  center={mapCenter}
                  zoom={6}
                  scrollWheelZoom={false}
                  className="dealer-map"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapBounds dealers={dealersWithCoords} />
                  {dealersWithCoords.map(dealer => (
                    <Marker
                      key={dealer.id}
                      position={[dealer.latitude!, dealer.longitude!]}
                      icon={DefaultIcon}
                    >
                      <Popup>
                        <strong>{dealer.name}</strong>
                        <br />
                        {dealer.address && <span>{dealer.address}, </span>}
                        {dealer.city}, {dealer.country}
                        {dealer.website && (
                          <>
                            <br />
                            <a href={dealer.website} target="_blank" rel="noopener noreferrer">
                              Website
                            </a>
                          </>
                        )}
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </div>
            <div className="dealer-list">
              {loadingDealers ? (
                <div className="loading">Lade Händlerstandorte...</div>
              ) : dealersError ? (
                <div className="dealer-list-empty">{dealersError}</div>
              ) : dealerLocations.length === 0 ? (
                <div className="dealer-list-empty">
                  Derzeit sind noch keine Händlerstandorte hinterlegt.
                </div>
              ) : (
                dealerLocations.map(dealer => (
                  <div className="dealer-row" key={dealer.id}>
                    <div>
                      <p className="dealer-name">{dealer.name}</p>
                      <p className="dealer-city">
                        {dealer.city}
                        {dealer.country ? ` · ${dealer.country}` : ''}
                        {dealer.website && (
                          <>
                            {' · '}
                            <a href={dealer.website} target="_blank" rel="noopener noreferrer">
                              Website
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                    <span className="dealer-badge">Verifiziert</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* ============ 5. B2B CTA BAND ============ */}
        <section id="b2b" className="b2b-section">
          <div className="b2b-inner" data-reveal="">
            <div>
              <p className="eyebrow">Für Optiker &amp; Sporthändler</p>
              <h2>Händler werden.</h2>
              <p>
                Erweitere dein Sortiment um Sportoptik mit echter Nachfrage. Schulung, Fertigung
                und Portal-Zugang — wir machen dich startklar für die Saison.
              </p>
            </div>
            <div className="b2b-cta">
              <Link to="/dealer-application" className="btn btn-outline">
                Jetzt Partner werden
              </Link>
              <p className="b2b-note">Kurzes Formular — Antwort innerhalb von 48 h.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
